<div class="card shadow">
  <div class="card-body">
    <div class="row align-items-center mb-3 mb-md-4 gy-2 position-relative z-1">
      <div class="col-auto">
        <!-- left side -->
        @if (hasAccounting()) {
          <div class="row align-items-center">
            <div class="col-auto">
              <button
                type="button"
                (click)="setViewPayments()"
                [ngClass]="{ 'btn-secondary': !showDeposits(), 'btn-light-on-white': showDeposits() }"
                class="btn btn-sm">
                Payments
              </button>
            </div>
            <div class="col-auto">
              <button
                type="button"
                (click)="setViewDeposits()"
                [ngClass]="{ 'btn-secondary': showDeposits(), 'btn-light-on-white': !showDeposits() }"
                class="btn btn-sm">
                Deposits
              </button>
            </div>
          </div>
        }
      </div>
      <div class="col-auto ms-auto">
        <!-- right side -->
        <div class="row align-items-center">
          <div class="col-auto">
            <button
              class="btn btn-sm"
              [disabled]="showDeposits()"
              [ngClass]="showSyncButton() || connectToSync() ? 'btn-outline-dark' : 'btn-primary'"
              (click)="onExport()">
              Export CSV
            </button>
          </div>
          @if (connectToSync() && !loadingActiveConnection()) {
            <div class="col-auto">
              @if (noTenantSelection() || (!sourceAccount() && hasActiveConnection())) {
                <a
                  routerLink="/accounting/integrations"
                  ngbTooltip="Complete your accounting setup on the Integrations page to enable syncing."
                  tooltipClass="custom-tooltip"
                  placement="bottom"
                  class="btn btn-primary btn-sm">
                  <i class="iconoir-warning-circle"></i> &nbsp;Sync transactions
                </a>
              } @else {
                <a
                  routerLink="/accounting/integrations"
                  class="btn btn-primary btn-sm">
                  Connect to sync
                </a>
              }
            </div>
          }
          @if (catSyncStatus() === AccountingSyncStatus.Busy) {
            <div class="col-auto">
              <button class="btn btn-primary btn-sm disabled">
                Sync transactions &nbsp;
                <i
                  [ngbTooltip]="'Category sync in progress...'"
                  tooltipClass="custom-tooltip"
                  class="iconoir-warning-circle">
                </i>
              </button>
            </div>
          } @else {
            @if (showSyncButton()) {
              <div class="col-auto">
                @if (unsyncedTransactions() > 0) {
                  <div
                    [ngbTooltip]="syncToolTip"
                    placement="bottom"
                    tooltipClass="custom-tooltip"
                    [disableTooltip]="syncEnabled() ?? true">
                    <button
                      [disabled]="!hasTransactions() || busySyncing() || !syncEnabled()"
                      class="btn btn-primary sync-btn btn-sm"
                      (click)="syncTransactions()">
                      @if (busySyncing()) {
                        Syncing {{ readyTransactions() ? readyTransactions() : '' }} transaction{{
                          readyTransactions() === 1 ? '' : 's'
                        }}
                        &nbsp;
                        <i class="spinner-border spinner-border-sm"></i>
                      } @else {
                        Sync transactions &nbsp;
                        <i
                          [ngbTooltip]="unsyncedTransactions() + ' transactions have not been synced'"
                          tooltipClass="custom-tooltip"
                          class="iconoir-warning-circle">
                        </i>
                      }
                      &nbsp;&nbsp;
                    </button>
                  </div>
                  <ng-template #syncToolTip>
                    @if (!syncEnabled()) {
                      Syncing is currently disabled.
                    }
                  </ng-template>
                }
                @if (!unsyncedTransactions() && !hasUnsyncedLoading()) {
                  <button
                    class="btn btn-primary sync-btn btn-sm"
                    disabled>
                    Synced &nbsp;&nbsp;
                    <i class="iconoir-check-circle text-success"></i>
                  </button>
                }
              </div>
            }
          }
        </div>
      </div>
    </div>

    <div
      class="transaction-filter-wrapper"
      [ngClass]="{ 'no-accounting': !hasAccounting() }">
      <app-table-header-template [searchOptions]="TransactionSearchOptions">
        <ng-container filter>
          <app-transaction-filter
            (query)="update($event)"
            [isDepositView]="showDeposits()"></app-transaction-filter>
        </ng-container>
      </app-table-header-template>
    </div>

    <ng-template
      #rowDetailTpl
      let-row="row">
      @let transaction = asTransaction(row);
      <div class="transaction-row-split-detail">
        <div class="split-items-title d-none d-md-block">
          <div class="row align-items-center flex-nowrap">
            <div class="col text-muted text-sm">Split Transaction</div>
            <div class="col-auto ps-0">
              <i class="pays-svg-icon ic-split text-secondary-2"></i>
            </div>
          </div>
        </div>
        <div class="split-items-wrapper">
          <table>
            <thead class="sticky-top bg-white">
              <tr class="text-muted text-sm">
                <th>Item name</th>
                <th>Tax Type</th>
                <th>Category</th>
                <th>Amount</th>
              </tr>
            </thead>
            @if (transaction.items && transaction.items.length > 0) {
              <tbody class="text-secondary-2">
                @for (item of transaction.items; track $index) {
                  <tr>
                    <td>{{ item.name }}</td>
                    <td>
                      @if (item.tax?.name) {
                        {{ item.tax?.name }} ({{ item.tax?.rate }}%)
                      } @else {
                        {{ returnVatTypeLabel(item.vat) }}
                      }
                    </td>
                    <td>{{ item.category.name }}</td>
                    <td>{{ item.amount | currency: 'ZAR' : 'symbol-narrow' | emptyIndicator }}</td>
                  </tr>
                }
              </tbody>
            }
          </table>
        </div>
      </div>
    </ng-template>

    <!-- Cell Templates (defined outside conditional to ensure they're always available) -->
    <ng-template
      #dateCell
      let-value="value">
      {{ value | date: 'dd/MM/yyyy' }}<br />
      <span class="small text-black-50">{{ value | date: 'hh:mm a' }}</span>
    </ng-template>
    <ng-template
      #contactCell
      let-row="row">
      @if (row.contact?.name) {
        {{ row.contact?.name }}
      } @else {
        <i
          class="iconoir-warning-circle warning-icon"
          ngbTooltip="No contact matched"
          tooltipClass="custom-tooltip"
          placement="right"></i>
      }
    </ng-template>
    <ng-template
      #merchantCell
      let-value="value">
      <div class="text-overflow-ellipses">
        {{ value }}
      </div>
    </ng-template>

    <ng-template
      #categoryCell
      let-row="row"
      let-value="value">
      @if (row.items.length > 0) {
        <div class="row align-items-center flex-nowrap">
          <div class="col-auto">
            <i class="pays-svg-icon ic-split text-secondary-2"></i>
          </div>
          <div class="col ps-0">
            <div class="text-overflow-ellipses">Split Transaction</div>
          </div>
        </div>
      } @else {
        <div class="d-flex flex-nowrap align-items-center bg-secondary">
          <div class="text-overflow-ellipses">
            @if (showCategoryAlert(row)) {
              <i
                class="iconoir-warning-circle warning-icon"
                [ngbTooltip]="tipContent"
                placement="right"
                tooltipClass="custom-tooltip"></i>
              <ng-template #tipContent>
                @if (row.category === null) {
                  No category selected
                } @else {
                  @if (hasAccounting() && hasActiveConnection() && !row.category?.account) {
                    Category is<br />
                    not supported
                  }
                  @if (!value.is_visible) {
                    Category has <br />
                    been disabled
                  }
                }
              </ng-template>
            }
            <span [ngClass]="{ 'text-disabled': !value.is_visible }">
              {{ value.name }}
              @if (value.account?.code) {
                <span>({{ value.account.code }})</span>
              }
            </span>
          </div>
        </div>
      }
    </ng-template>
    <ng-template
      #amountCell
      let-row="row"
      let-value="value">
      <span
        class="fw-semibold"
        [class.opacity-50]="row.custom_amount">
        {{ value | currency: 'ZAR' : 'symbol-narrow' | emptyIndicator }}
      </span>
    </ng-template>
    <ng-template
      #receiptCell
      let-row="row"
      let-value="value">
      <div class="lead text-center">
        @if (value) {
          <i class="iconoir-check-circle text-success"></i>
        } @else if (row.receipt) {
          <i class="iconoir-check-circle text-success"></i>
        } @else {
          <i class="iconoir-minus-circle text-danger"></i>
        }
      </div>
    </ng-template>
    <ng-template
      #statusCell
      let-row="row">
      @if (statusSynched(row) || statusError(row)) {
        <app-status-badge
          [value]="row.status"
          [department]="statusBadgeDepartments.TRANSACTIONS">
        </app-status-badge>
      } @else {
        <div class="text-center">
          <div
            ngbTooltip="Accounting software connection is not active"
            tooltipClass="custom-tooltip"
            [disableTooltip]="
              (hasActiveConnection() && (statusReady(row) || statusNew(row))) ||
              (!hasActiveConnection() && statusReady(row))
            ">
            @if (showContactErrorOnReady(row) || showTaxIdErrorOnReady(row)) {
              <i
                class="iconoir-warning-circle danger-icon-contact"
                tooltipClass="custom-tooltip"
                [ngbTooltip]="tipContentReadyStatusError"
                placement="bottom">
              </i>
              <ng-template #tipContentReadyStatusError>
                Untoggle ready status in <br />
                order to fix errors.
              </ng-template>
            }
            <app-toggle-label-switch
              [initialValue]="statusReady(row)"
              [label]="statusReady(row) ? 'Ready to sync' : 'Mark ready'"
              [id]="'toggle_' + row.id"
              [disabled]="markReadyButtonDisabled(row)"
              (switchChange)="$event ? markTransactionReady(row.id) : unmarkTransactionReady(row.id)">
            </app-toggle-label-switch>
          </div>
        </div>
      }
    </ng-template>
    <ng-template
      #actionsCell
      let-row="row">
      <button
        class="btn-action"
        title="View transaction"
        (click)="onViewTransaction(row.id)">
        <i class="iconoir-nav-arrow-right"></i>
      </button>
    </ng-template>

    <!-- View Toggle -->
    @if (showDeposits()) {
      <app-deposits [suppressFetch]="isNavigating"></app-deposits>
    } @else {
      <app-table
        [columns]="columns"
        [getDataCallback]="getData"
        [rowDetailTemplate]="rowDetailTpl"
        [canExpandRow]="canExpandDocument"
        [suppressFetch]="isNavigating"
        (dataLoaded)="onDataLoaded($event)"
        [rowDetailHeight]="136"
        #table
        class="table-recent-transactions"
        emptyMessage="Your recent transactions will appear here.">
      </app-table>
    }
  </div>
</div>

<div id="offCanvasGhostContainer">
  <router-outlet />
</div>
